import React from 'react';
import PropTypes from 'prop-types';
import { Scheduler } from '@reactivex/rxjs';
import shallowEqual from 'react-recompose/shallowEqual';
import mapPropsStream from 'react-recompose/mapPropsStream';
import compose from 'react-recompose/compose';
import getContext from 'react-recompose/getContext';

import { pointSizes, cameraChanges, hitmapUpdates } from '@graphistry/viz/src/client/legacy';

import styles from './styles.less';

const cameraUpdates = cameraChanges
    .merge(hitmapUpdates)
    .auditTime(0, Scheduler.animationFrame)
    .startWith({})
    .map(() => Scheduler.animationFrame.now());

const keysThatCanCauseRenders = ['axis', 'sceneUpdateTime'];
const WithCamera = mapPropsStream(propsStream =>
    propsStream
        .combineLatest(cameraUpdates, (props, sceneUpdateTime) => ({
            ...props,
            sceneUpdateTime
        }))
        .distinctUntilChanged((prev, curr) =>
            keysThatCanCauseRenders.every(key => shallowEqual(prev[key], curr[key]))
        )
        .withLatestFrom(pointSizes.map(({ buffer }) => new Uint8Array(buffer)), (props, sizes) => ({
            ...props,
            sizes
        }))
);

function AxisReact(props) {
    const { renderState: { camera, canvas } = {}, sizes = [], axis = [] } = props;
    if (!camera || !canvas) {
        return null;
    }
    const matrix = camera.getMatrix();

    const { x: minXTranslated, y: y0 } = camera.canvasCoords(0, (axis[0]||{}).y || 0, canvas, matrix);
    const { x: maxXTranslated, y: y1 } = camera.canvasCoords((axis[0]||{}).width || 0, (axis[1]||{}).y || 0, canvas, matrix);
    const ySkipRate = Math.ceil(16 / (y1 - y0)) || 1;

    return (
        <div className={styles['axis']}>
            {axis.map(({ label, y: labelY, r: labelR, bounds = {}, width }, i) => {
                if (typeof labelY !== 'undefined') {
                    if (i % ySkipRate > 0) { return undefined; }                
                    const { min, max } = bounds;
                    const { y: yTranslated } = camera.canvasCoords(0, labelY, canvas, matrix);
                    return (
                        <div
                            key={`line-axis-${labelY}-${i}`}
                            className={styles['straightline']}
                            style={{ transform: `translate3d(10px, ${yTranslated}px, 0)` }}>
                            <span className={styles['straightlabel']}>{label}</span>
                            { min !== undefined  && minXTranslated > 100 ?
                                    <span 
                                        className={styles['straightlabel-bookend-start']}
                                        style={{ transform: `translate3d(${Math.round(minXTranslated)}px, 0px, 0)` }}
                                        >{min}</span>
                                    : undefined }
                            { max !== undefined && maxXTranslated > 100 ?
                                    <span 
                                        className={styles['straightlabel-bookend-stop']}
                                        style={{ transform: `translate3d(${Math.round(maxXTranslated)}px, 0px, 0)` }}
                                        >{max}</span>
                                    : undefined }
                        </div>
                    );
                } else if (typeof labelR !== 'undefined') {
                    const { x: xMin, y: yMin } = camera.canvasCoords(
                        -labelR,
                        +labelR,
                        canvas,
                        matrix
                    );
                    const { x: xMax, y: yMax } = camera.canvasCoords(
                        +labelR,
                        -labelR,
                        canvas,
                        matrix
                    );
                    return (
                        <div
                            key={`radial-axis-${labelR}-${i}`}
                            className={styles['radial-axis']}
                            style={{
                                width: xMax - xMin,
                                transform: `translate3d(${xMin}px, ${yMin}px, 0)`
                            }}>
                            <span className={styles['radial-axis-label']}>{label}</span>
                        </div>
                    );
                }
            })}
        </div>
    );
}

const Axis = compose(
    getContext({
        renderState: PropTypes.object
    }),
    WithCamera
)(AxisReact);

export { Axis };
